import { esErrorApi } from '../api';

/**
 * Qué se le muestra a una persona cuando falla una operación del ABM (KAN-143).
 *
 * # SE DECIDE SOBRE EL CÓDIGO, NUNCA SOBRE EL DETALLE
 *
 * Es la misma regla que en el login, y acá se nota más porque hay dos `409`
 * distintos: `ya_existe` (email repetido) y `operacion_invalida` (las reglas de
 * la fila propia). Distinguirlos por el texto —`detalle.includes('email')`— se
 * rompería el día que alguien corrija una redacción del backend, y en esta
 * pantalla eso significaría mostrar el cartel equivocado al lado del campo
 * equivocado.
 *
 * # POR QUÉ EL MENSAJE DEPENDE TAMBIÉN DE LA OPERACIÓN
 *
 * `operacion_invalida` es un solo código para las dos reglas: el backend no
 * distingue "no te des de baja" de "no te degrades" con códigos distintos. Lo que
 * sí sabe el front, sin leer ningún texto, es **qué botón se apretó**. De ahí
 * sale el mensaje, y por eso {@link mensajeDeUsuarios} pide la operación.
 *
 * El error **por campo** (`campos` del `400`) no está acá: es `mensajeDeCampo`,
 * en `src/api`, porque no tiene nada de usuarios. Se levantó ahí en KAN-144, al
 * escribirse el segundo ABM.
 */

/** La operación que falló. La sabe quien la disparó; no se deduce de la respuesta. */
export type Operacion = 'alta' | 'modificar' | 'password' | 'desactivar' | 'activar';

const GENERICO = 'No se pudo completar la operación. Probá de nuevo.';

/** Convierte lo que haya fallado en un texto mostrable. */
export function mensajeDeUsuarios(error: unknown, operacion: Operacion): string {
  if (!esErrorApi(error)) {
    return GENERICO;
  }
  switch (error.codigo) {
    case 'ya_existe':
      return 'Ya hay un usuario con ese email. Si está dado de baja, reactivalo desde la lista en vez de crearlo de nuevo.';
    case 'operacion_invalida':
      return operacion === 'desactivar'
        ? 'No podés darte de baja a vos mismo. Pedíselo a otro administrador.'
        : 'No podés quitarte el rol de administrador a vos mismo. Pedíselo a otro administrador.';
    case 'request_invalida':
      // El detalle por campo va al lado de cada campo (ver `mensajeDeCampo`);
      // esto es solo el encabezado que dice dónde mirar.
      return 'Revisá los campos marcados.';
    case 'no_encontrado':
      return 'Ese usuario ya no existe. Actualizá la lista.';
    case 'no_autenticado':
      // Casi imposible de ver: el 401 cierra la sesión y encima aparece el
      // diálogo de reingreso. Está por completitud, no porque se espere.
      return 'Tu sesión ya no vale. Volvé a entrar.';
    case 'prohibido':
      return 'Tu usuario ya no tiene permiso de administración.';
    case 'sin_conexion':
      return 'No se pudo contactar al servicio. Revisá tu conexión e intentá de nuevo.';
    case 'error_interno':
      return 'El servicio tuvo un problema. Intentá de nuevo en un momento.';
    default:
      return GENERICO;
  }
}
